import { ListItem, ListItemButton, ListItemIcon, ListItemText, SvgIcon } from '@mui/material';
import getMuiIcon, { IconName } from '../../../utils/getMuiIcon';
import { useTranslation } from 'react-i18next';
import React, { useMemo } from 'react';
import { borderRadius, customSizes, palette, spaces, typography } from '../../../assets/styles/themes/tokens';
import { Link } from 'found';
import useRouter from '../../../utils/useRouter';
import MenuItemTooltip from './MenuItemTooltip';

interface MenuItemProps {
  icon: IconName;
  label: string;
  rightSection?: React.ReactNode;
  to?: string;
  external?: boolean;
  isCollapsed: boolean;
  onClick?: () => void;
  isFocused?: boolean;
  activePaths?: string[];
}

const listItemIconStyles = {
  minWidth: '36px',
  width: '36px',
  color: palette.text.secondary,
  padding: 0,
};

const listItemTextStyles = {
  margin: '0px',
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: spaces.sm,
};

const activeStyles = {
  backgroundColor: palette.primary.selected,
  '& .MuiListItemText-primary': {
    color: palette.primary.main,
  },
  '& .MuiSvgIcon-root': {
    color: palette.primary.main,
  },
};

const focusedStyles = {
  backgroundColor: palette.action.focus,
};

const getListItemButtonStyles = (isActive: boolean, isFocused: boolean) => ({
  paddingX: spaces.sm,
  height: customSizes.sideNavbar.itemHeight,
  borderRadius: borderRadius.sm,
  ...(isFocused && focusedStyles),
  ...(isActive && activeStyles),
  '&:hover': activeStyles,
});

const MenuItem: React.FC<MenuItemProps> = ({
  icon,
  label,
  to,
  rightSection,
  external = false,
  isCollapsed,
  onClick = () => {},
  isFocused = false,
  activePaths,
}) => {
  const { t } = useTranslation();
  const { match } = useRouter();

  const isActive = useMemo(() => {
    if (external || !to) return false;
    const currentPath = match.location.pathname.replace(/^\//, '');
    if (activePaths) {
      return activePaths.some((activePath) => currentPath.startsWith(activePath));
    } else {
      const targetPath = to.split('?')[0].replace(/^\//, '');
      return currentPath.startsWith(targetPath);
    }
  }, [external, to, match.location.pathname,]);
  
  const linkProps = useMemo(() => external
    ? {
      component: 'a',
      href: to,
      target: '_blank',
      rel: 'noopener noreferrer',
    }
    : {
      component: Link,
      to: `/${to}`,
    }, [external, to]);

  const content = (
    <ListItem disablePadding {...linkProps} onClick={onClick}>
      <ListItemButton 
        sx={getListItemButtonStyles(isActive, isFocused)} 
        data-active={isActive}
        data-focused={isFocused}
      >
        <ListItemIcon sx={listItemIconStyles}>
          <SvgIcon component={getMuiIcon(icon)} />
        </ListItemIcon>
        {!isCollapsed && (
          <ListItemText
            primary={t(label)}
            secondary={rightSection}
            sx={listItemTextStyles}
            primaryTypographyProps={{
              ...typography.caption,
              color: palette.text.primary,
              lineHeight: '1.75rem',
              letterSpacing: '0.15px',
            }}
          />
        )}
      </ListItemButton>
    </ListItem>
  );

  return (
    isCollapsed ? (
      <MenuItemTooltip title={t(label)}>
        <span>
          {content}
        </span>
      </MenuItemTooltip>
    ) : content
  );
};

export default MenuItem;
